import React, { useState, useEffect, useRef } from 'react';
import { formatCurrency } from '../utils/formatters';
import {
  ClipboardCheck, Plus, Search, Calendar, CheckCircle, XCircle, Clock,
  AlertTriangle, Camera, FileText, User, MapPin, Edit, Eye, Trash2,
  Download, Filter, Settings, ArrowLeft, Save, Power
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import SignatureCanvas from 'react-signature-canvas';
import InspectionReportModal from '../components/InspectionReportModal';

// ===== DATE RANGE PICKER =====
const PRESETS = [
  { label: 'Hoje', key: 'today' },
  { label: 'Esta semana', key: 'this_week' },
  { label: 'Este mês', key: 'this_month' },
  { label: 'Mês passado', key: 'last_month' },
  { label: 'Próximos 7 dias', key: 'next_7' },
  { label: 'Próximos 30 dias', key: 'next_30' },
  { label: 'Personalizado', key: 'custom' },
];

function getPresetRange(key) {
  const today = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];
  const startOfWeek = (d) => {
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.getFullYear(), d.getMonth(), diff);
  };
  switch (key) {
    case 'today': { const f = fmt(today); return { from: f, to: f }; }
    case 'this_week': {
      const start = startOfWeek(new Date(today));
      const end = new Date(start); end.setDate(start.getDate() + 6);
      return { from: fmt(start), to: fmt(end) };
    }
    case 'this_month': {
      return { from: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), to: fmt(new Date(today.getFullYear(), today.getMonth() + 1, 0)) };
    }
    case 'last_month': {
      return { from: fmt(new Date(today.getFullYear(), today.getMonth() - 1, 1)), to: fmt(new Date(today.getFullYear(), today.getMonth(), 0)) };
    }
    case 'next_7': {
      const to = new Date(today); to.setDate(today.getDate() + 7);
      return { from: fmt(today), to: fmt(to) };
    }
    case 'next_30': {
      const to = new Date(today); to.setDate(today.getDate() + 30);
      return { from: fmt(today), to: fmt(to) };
    }
    default: return { from: '', to: '' };
  }
}

function DateRangePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(value.from || '');
  const [customTo, setCustomTo] = useState(value.to || '');
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectPreset = (key) => {
    if (key === 'custom') { onChange({ preset: 'custom', from: customFrom, to: customTo }); return; }
    const range = getPresetRange(key);
    setCustomFrom(range.from); setCustomTo(range.to);
    onChange({ preset: key, ...range }); setOpen(false);
  };

  const applyCustom = () => { onChange({ preset: 'custom', from: customFrom, to: customTo }); setOpen(false); };

  const formatLabel = () => {
    if (!value.preset && !value.from) return 'Todas as datas';
    if (value.preset && value.preset !== 'custom') return PRESETS.find(p => p.key === value.preset)?.label || 'Todas as datas';
    if (value.from && value.to) {
      const fmt = (s) => { const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
      return `${fmt(value.from)} – ${fmt(value.to)}`;
    }
    if (value.from) return `A partir de ${value.from}`;
    return 'Todas as datas';
  };

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(!open)}
        className={`w-full px-4 py-2 border-2 rounded-lg bg-white dark:bg-gray-700 text-left flex items-center justify-between gap-2 hover:border-orange-400 transition text-sm ${value.from ? 'border-orange-400 text-orange-700 dark:text-orange-300 font-medium' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-white'}`}
      >
        <span className="truncate">{formatLabel()}</span>
        <svg className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg min-w-[260px]">
          <div className="p-2">
            {PRESETS.filter(p => p.key !== 'custom').map(preset => (
              <button key={preset.key} type="button" onClick={() => selectPreset(preset.key)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${value.preset === preset.key ? 'bg-orange-500 text-white font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              >{preset.label}</button>
            ))}
            <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
              <p className="text-xs text-gray-500 px-3 mb-2 font-medium uppercase tracking-wide">Personalizado</p>
              <div className="px-3 space-y-2">
                <div className="flex gap-2 items-center">
                  <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                  <span className="text-gray-400 text-sm">–</span>
                  <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>
                <button type="button" onClick={applyCustom}
                  className="w-full py-1.5 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition">
                  Aplicar
                </button>
              </div>
            </div>
            {(value.preset || value.from) && (
              <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2 px-2">
                <button type="button" onClick={() => { onChange({ preset: '', from: '', to: '' }); setOpen(false); }}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
                  Limpar filtro de data
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * UNIFIED INSPECTIONS PAGE
 *
 * Consolidates functionality from:
 * - ChecklistsPage.jsx (DVIR checklists)
 * - VehicleInspectionPage.jsx (predictive maintenance)
 *
 * Integrates with unified backend API: /api/inspections
 *
 * Supports 3 inspection types:
 * 1. Checklist Inspections (daily, weekly, pre-trip, post-trip)
 * 2. Vehicle Inspections (predictive maintenance)
 * 3. Contract Inspections (check-in, check-out)
 */
const InspectionsPage = () => {
  // ==========================================
  // STATE MANAGEMENT
  // ==========================================

  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Dashboard & Lists
  const [dashboard, setDashboard] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [dateRange, setDateRange] = useState({ preset: '', from: '', to: '' });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Modals
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);

  // Selected Items
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Template Form
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    category: 'checklist',
    vehicle_type: '',
    requires_signature: true,
    requires_photos: false,
    requires_video: false,
    sections: []
  });

  // Inspection Form
  const [inspectionForm, setInspectionForm] = useState({
    template_id: '',
    vehicle_id: '',
    driver_id: '',
    scheduled_date: '',
    odometer_reading: '',
    engine_hours: '',
    location: '',
    notes: '',
    condutor_nome: '',
    recebedor_nome: '',
    checklist_entrega: {},
    items: []
  });

  // Signature Refs
  const signatureDriverRef = useRef();
  const signatureMechanicRef = useRef();
  const signatureCondutorRef = useRef();
  const signatureRecebedorRef = useRef();

  // ==========================================
  // DATA LOADING
  // ==========================================

  useEffect(() => {
    loadData();
  }, [activeTab, filterStatus, filterCategory, filterDateFrom, filterDateTo, dateRange]);

  const loadData = async () => {
    try {
      setLoading(true);

      if (activeTab === 'dashboard') {
        const res = await api.get('/api/inspections/stats');
        setDashboard(res.data.data);
      }
      else if (activeTab === 'inspections') {
        const params = {};
        if (filterStatus !== 'all') params.status = filterStatus;
        // filterCategory is now client-side (vehicle_category)
        const dateFrom = dateRange.from || filterDateFrom;
        const dateTo = dateRange.to || filterDateTo;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;

        const res = await api.get('/api/inspections', { params });
        setInspections(res.data.data || []);
      }
      else if (activeTab === 'templates') {
        const res = await api.get('/api/inspections/templates');
        setTemplates(res.data.data || []);
      }
      else if (activeTab === 'pending') {
        const res = await api.get('/api/inspections/pending', {
          params: { daysAhead: 30 }
        });
        setInspections(res.data.data || []);
      }
      else if (activeTab === 'overdue') {
        const res = await api.get('/api/inspections/overdue');
        setInspections(res.data.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadVehiclesAndDrivers = async () => {
    try {
      const [vehiclesRes, driversRes, typesRes] = await Promise.all([
        api.get('/api/vehicles'),
        api.get('/api/drivers'),
        api.get('/api/vehicle-types')
      ]);
      setVehicles(vehiclesRes.data.data || vehiclesRes.data || []);
      setDrivers(driversRes.data.data || driversRes.data || []);
      setVehicleTypes(typesRes.data.data || typesRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar veículos e motoristas:', error);
    }
  };

  // ==========================================
  // TEMPLATE MANAGEMENT
  // ==========================================

  const handleCreateTemplate = async () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      description: '',
      category: 'checklist',
      vehicle_type: '',
      requires_signature: true,
      requires_photos: false,
      requires_video: false,
      sections: []
    });
    await loadVehiclesAndDrivers();
    setShowTemplateModal(true);
  };

  const handleEditTemplate = async (template) => {
    try {
      const [res] = await Promise.all([
        api.get(`/api/inspections/templates/${template.id}`),
        loadVehiclesAndDrivers()
      ]);
      const fullTemplate = res.data.data;

      setEditingTemplate(template);
      setTemplateForm({
        name: fullTemplate.name,
        description: fullTemplate.description || '',
        category: fullTemplate.category || 'checklist',
        vehicle_type: fullTemplate.vehicle_type || '',
        requires_signature: fullTemplate.requires_signature,
        requires_photos: fullTemplate.requires_photos,
        requires_video: fullTemplate.requires_video || false,
        sections: fullTemplate.sections || []
      });
      setShowTemplateModal(true);
    } catch (error) {
      toast.error('Erro ao carregar template');
    }
  };

  const handleToggleTemplate = async (templateId, currentStatus) => {
    try {
      const res = await api.put(`/api/inspections/templates/${templateId}/toggle-active`);
      // Backend retorna is_active (snake_case do PostgreSQL)
      const newStatus = res.data.data?.is_active ?? res.data.data?.active;
      toast.success(newStatus ? 'Template ativado!' : 'Template desativado!');
      loadData();
    } catch (error) {
      console.error('Erro ao alternar status do template:', error);
      const errorMsg = error.response?.data?.message || 'Erro ao alternar status do template';
      toast.error(errorMsg);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.category) {
      toast.error('Nome e categoria são obrigatórios');
      return;
    }

    if (templateForm.sections.length === 0) {
      toast.error('Adicione pelo menos uma seção');
      return;
    }

    // Validar se todos os items têm nome
    for (let i = 0; i < templateForm.sections.length; i++) {
      const section = templateForm.sections[i];
      if (!section.name || section.name.trim() === '') {
        toast.error(`Seção ${i + 1} precisa ter um nome`);
        return;
      }

      if (section.items && section.items.length > 0) {
        for (let j = 0; j < section.items.length; j++) {
          const item = section.items[j];
          if (!item.name || item.name.trim() === '') {
            toast.error(`Item ${j + 1} da seção "${section.name}" precisa ter um nome`);
            return;
          }
        }
      }
    }

    try {
      if (editingTemplate) {
        await api.put(`/api/inspections/templates/${editingTemplate.id}`, templateForm);
        toast.success('Template atualizado com sucesso!');
      } else {
        await api.post('/api/inspections/templates', templateForm);
        toast.success('Template criado com sucesso!');
      }

      setShowTemplateModal(false);
      loadData();
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      toast.error('Erro ao salvar template');
    }
  };

  // Template Section/Item Management
  const addSection = () => {
    setTemplateForm({
      ...templateForm,
      sections: [
        ...templateForm.sections,
        {
          name: '',
          description: '',
          display_order: templateForm.sections.length + 1,
          items: []
        }
      ]
    });
  };

  const updateSection = (index, field, value) => {
    const newSections = [...templateForm.sections];
    newSections[index][field] = value;
    setTemplateForm({ ...templateForm, sections: newSections });
  };

  const removeSection = (index) => {
    const newSections = templateForm.sections.filter((_, i) => i !== index);
    setTemplateForm({ ...templateForm, sections: newSections });
  };

  const addItem = (sectionIndex) => {
    const newSections = [...templateForm.sections];
    newSections[sectionIndex].items.push({
      name: '',
      description: '',
      field_type: 'pass_fail',
      is_required: true,
      require_photo_if_fail: false,
      display_order: newSections[sectionIndex].items.length + 1
    });
    setTemplateForm({ ...templateForm, sections: newSections });
  };

  const updateItem = (sectionIndex, itemIndex, field, value) => {
    const newSections = [...templateForm.sections];
    newSections[sectionIndex].items[itemIndex][field] = value;
    setTemplateForm({ ...templateForm, sections: newSections });
  };

  const removeItem = (sectionIndex, itemIndex) => {
    const newSections = [...templateForm.sections];
    newSections[sectionIndex].items = newSections[sectionIndex].items.filter((_, i) => i !== itemIndex);
    setTemplateForm({ ...templateForm, sections: newSections });
  };

  // Função para marcar/desmarcar foto obrigatória em todos os itens de uma seção
  const toggleSectionPhotos = (sectionIndex, value) => {
    const newSections = [...templateForm.sections];
    newSections[sectionIndex].items = newSections[sectionIndex].items.map(item => ({
      ...item,
      require_photo_if_fail: value
    }));
    setTemplateForm({ ...templateForm, sections: newSections });
  };

  // Verificar se todos os itens de uma seção têm foto obrigatória
  const allSectionPhotosRequired = (sectionIndex) => {
    const section = templateForm.sections[sectionIndex];
    if (!section.items || section.items.length === 0) return false;
    return section.items.every(item => item.require_photo_if_fail);
  };

  // ==========================================
  // INSPECTION EXECUTION
  // ==========================================

  const handleStartInspection = async (template) => {
    setSelectedTemplate(template);

    try {
      // Load full template with sections and items
      const res = await api.get(`/api/inspections/templates/${template.id}`);
      const fullTemplate = res.data.data;

      // Initialize inspection items
      const items = [];
      fullTemplate.sections.forEach(section => {
        section.items.forEach(item => {
          items.push({
            template_item_id: item.id,
            section_name: section.name,
            item_name: item.name,
            field_type: item.field_type,
            is_required: item.is_required,
            require_photo_if_fail: item.require_photo_if_fail,
            response_value: '',
            is_approved: null,
            comments: '',
            photos: []
          });
        });
      });

      setInspectionForm({
        ...inspectionForm,
        template_id: template.id,
        items
      });

      await loadVehiclesAndDrivers();
      setShowInspectionModal(true);
    } catch (error) {
      console.error('Erro ao carregar template:', error);
      toast.error('Erro ao carregar template');
    }
  };

  const handleItemResponse = (index, field, value) => {
    const newItems = [...inspectionForm.items];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };

    // Auto-determine approval based on field type
    if (field === 'response_value') {
      if (newItems[index].field_type === 'pass_fail') {
        newItems[index].is_approved = value === 'pass';
      } else if (newItems[index].field_type === 'yes_no') {
        newItems[index].is_approved = value === 'yes';
      }
    }

    setInspectionForm({ ...inspectionForm, items: newItems });
  };

  const handlePhotoUpload = async (itemIndex, files) => {
    try {
      const newItems = [...inspectionForm.items];
      newItems[itemIndex].photos = Array.from(files).map(file => ({
        file,
        preview: URL.createObjectURL(file)
      }));
      setInspectionForm({ ...inspectionForm, items: newItems });
      toast.success('Fotos adicionadas');
    } catch (error) {
      toast.error('Erro ao adicionar fotos');
    }
  };

  const handleSubmitInspection = async () => {
    try {
      // Validate required fields
      if (!inspectionForm.vehicle_id) {
        toast.error('Selecione um veículo');
        return;
      }

      // Validate signature if required
      if (selectedTemplate?.requires_signature) {
        if (!signatureDriverRef.current || signatureDriverRef.current.isEmpty()) {
          toast.error('Assinatura do motorista é obrigatória');
          return;
        }
      }

      setSaving(true);

      // Get signatures
      const signature_driver = signatureDriverRef.current?.toDataURL();
      const signature_mechanic = signatureMechanicRef.current?.toDataURL();
      const signature_condutor = signatureCondutorRef.current && !signatureCondutorRef.current.isEmpty() ? signatureCondutorRef.current.toDataURL() : null;
      const signature_recebedor = signatureRecebedorRef.current && !signatureRecebedorRef.current.isEmpty() ? signatureRecebedorRef.current.toDataURL() : null;

      // Create inspection
      const inspectionData = {
        template_id: inspectionForm.template_id,
        vehicle_id: inspectionForm.vehicle_id,
        driver_id: inspectionForm.driver_id || null,
        scheduled_date: inspectionForm.scheduled_date || new Date().toISOString(),
        odometer_reading: inspectionForm.odometer_reading ? parseInt(inspectionForm.odometer_reading) : null,
        engine_hours: inspectionForm.engine_hours ? parseInt(inspectionForm.engine_hours) : null,
        location: inspectionForm.location || null,
        notes: inspectionForm.notes || null,
        condutor_nome: inspectionForm.condutor_nome || null,
        recebedor_nome: inspectionForm.recebedor_nome || null,
        signature_driver,
        signature_mechanic,
        signature_condutor,
        signature_recebedor
      };

      const res = await api.post('/api/inspections', inspectionData);
      const inspectionId = res.data.data.id;

      // Start inspection
      await api.post(`/api/inspections/${inspectionId}/start`, {
        started_by: 'inspector'
      });

      // Save inspection items
      for (let i = 0; i < inspectionForm.items.length; i++) {
        const item = inspectionForm.items[i];
        await api.post(`/api/inspections/${inspectionId}/items`, {
          template_item_id: item.template_item_id,
          response_value: item.response_value,
          is_approved: item.is_approved,
          comments: item.comments
        });
      }

      // Upload photos
      for (let i = 0; i < inspectionForm.items.length; i++) {
        const item = inspectionForm.items[i];
        if (item.photos && item.photos.length > 0) {
          const formData = new FormData();
          item.photos.forEach(photoObj => {
            formData.append('photos', photoObj.file);
          });

          await api.post(`/api/inspections/${inspectionId}/photos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      }

      // Complete inspection
      await api.post(`/api/inspections/${inspectionId}/complete`, {
        notes: inspectionForm.notes,
        condutor_nome: inspectionForm.condutor_nome || null,
        recebedor_nome: inspectionForm.recebedor_nome || null,
        signature_condutor,
        signature_recebedor,
        checklist_entrega: inspectionForm.checklist_entrega || {}
      });

      toast.success('Inspeção criada com sucesso!');
      setShowInspectionModal(false);
      loadData();
    } catch (error) {
      console.error('Erro ao criar inspeção:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar inspeção');
    } finally {
      setSaving(false);
    }
  };

  const handleViewInspection = async (inspection) => {
    try {
      const res = await api.get(`/api/inspections/${inspection.id}`);
      setSelectedInspection(res.data.data);
      setShowViewModal(true);
    } catch (error) {
      toast.error('Erro ao carregar inspeção');
    }
  };


  // ==========================================
  // RENDER HELPERS
  // ==========================================

  const renderFieldInput = (item, index) => {
    switch (item.field_type) {
      case 'pass_fail':
        return (
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name={`item-${index}`}
                value="pass"
                checked={item.response_value === 'pass'}
                onChange={(e) => handleItemResponse(index, 'response_value', e.target.value)}
                className="w-4 h-4"
              />
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-700 dark:text-green-400">Aprovado</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name={`item-${index}`}
                value="fail"
                checked={item.response_value === 'fail'}
                onChange={(e) => handleItemResponse(index, 'response_value', e.target.value)}
                className="w-4 h-4"
              />
              <XCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-700 dark:text-red-400">Reprovado</span>
            </label>
          </div>
        );

      case 'yes_no':
        return (
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name={`item-${index}`}
                value="yes"
                checked={item.response_value === 'yes'}
                onChange={(e) => handleItemResponse(index, 'response_value', e.target.value)}
                className="w-4 h-4"
              />
              <span>Sim</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name={`item-${index}`}
                value="no"
                checked={item.response_value === 'no'}
                onChange={(e) => handleItemResponse(index, 'response_value', e.target.value)}
                className="w-4 h-4"
              />
              <span>Não</span>
            </label>
          </div>
        );

      case 'numeric':
        return (
          <input
            type="number"
            value={item.response_value || ''}
            onChange={(e) => handleItemResponse(index, 'response_value', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
            placeholder="Digite o valor"
          />
        );

      case 'text':
        return (
          <textarea
            value={item.response_value || ''}
            onChange={(e) => handleItemResponse(index, 'response_value', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
            rows={2}
            placeholder="Digite sua resposta"
          />
        );

      default:
        return <span className="text-gray-500">Tipo de campo não suportado</span>;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      requires_attention: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendente',
      scheduled: 'Agendado',
      in_progress: 'Em Andamento',
      completed: 'Concluído',
      approved: 'Aprovado',
      cancelled: 'Cancelado',
      requires_attention: 'Requer Atenção',
      failed: 'Reprovado'
    };
    return labels[status] || status;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      checklist: 'Checklist',
      vehicle: 'Veículo',
      vehicle_inspection: 'Inspeção de Veículo',
      contract: 'Contrato',
      maintenance: 'Manutenção',
      preventive: 'Preventiva',
      corrective: 'Corretiva',
      post_repair: 'Pós-Reparo',
    };
    return labels[category] || category;
  };

  // ==========================================
  // RENDER
  // ==========================================

  if (loading && !dashboard && !inspections.length && !templates.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <ClipboardCheck className="w-8 h-8 text-blue-600" />
            Sistema de Inspeções Unificado
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gestão completa de inspeções veiculares e contratuais
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8 overflow-x-auto">
          {['dashboard', 'inspections', 'templates', 'pending', 'overdue'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab === 'dashboard' && 'Dashboard'}
              {tab === 'inspections' && 'Inspeções'}
              {tab === 'templates' && 'Templates'}
              {tab === 'pending' && 'Pendentes'}
              {tab === 'overdue' && 'Atrasadas'}
            </button>
          ))}
        </nav>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboard && (
        <div className="space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total de Inspeções</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                    {dashboard.total_inspections}
                  </p>
                </div>
                <FileText className="w-12 h-12 text-blue-600" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Aprovadas</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                    {dashboard.approved}
                  </p>
                </div>
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-red-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Reprovadas</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                    {dashboard.failed}
                  </p>
                </div>
                <XCircle className="w-12 h-12 text-red-600" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-yellow-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Pendentes</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                    {dashboard.pending}
                  </p>
                </div>
                <Clock className="w-12 h-12 text-yellow-600" />
              </div>
            </div>
          </div>

          {/* Additional KPIs Row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Em Andamento</p>
                  <p className="text-3xl font-bold mt-2">
                    {dashboard.in_progress || 0}
                  </p>
                </div>
                <Clock className="w-12 h-12 opacity-80" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Atrasadas</p>
                  <p className="text-3xl font-bold mt-2">
                    {dashboard.overdue || 0}
                  </p>
                </div>
                <AlertTriangle className="w-12 h-12 opacity-80" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Health Score Médio</p>
                  <p className="text-3xl font-bold mt-2">
                    {dashboard.avg_health_score ? Number(dashboard.avg_health_score).toFixed(0) : '0'}
                  </p>
                </div>
                <CheckCircle className="w-12 h-12 opacity-80" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Penalidades</p>
                  <p className="text-3xl font-bold mt-2">
                    {formatCurrency(dashboard.total_penalties || 0)}
                  </p>
                </div>
                <XCircle className="w-12 h-12 opacity-80" />
              </div>
            </div>
          </div>

          {/* Statistics Dashboard */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Distribution Chart */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Distribuição por Status
              </h3>
              <div className="space-y-4">
                {/* Completed */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Concluídas</span>
                    <span className="text-sm font-bold text-green-600">
                      {dashboard.completed} ({dashboard.total_inspections > 0 ? ((dashboard.completed / dashboard.total_inspections) * 100).toFixed(0) : 0}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div
                      className="bg-green-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${dashboard.total_inspections > 0 ? (dashboard.completed / dashboard.total_inspections) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* In Progress */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Em Andamento</span>
                    <span className="text-sm font-bold text-purple-600">
                      {dashboard.in_progress || 0} ({dashboard.total_inspections > 0 ? (((dashboard.in_progress || 0) / dashboard.total_inspections) * 100).toFixed(0) : 0}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div
                      className="bg-purple-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${dashboard.total_inspections > 0 ? ((dashboard.in_progress || 0) / dashboard.total_inspections) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Pending */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Pendentes</span>
                    <span className="text-sm font-bold text-yellow-600">
                      {dashboard.pending} ({dashboard.total_inspections > 0 ? ((dashboard.pending / dashboard.total_inspections) * 100).toFixed(0) : 0}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div
                      className="bg-yellow-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${dashboard.total_inspections > 0 ? (dashboard.pending / dashboard.total_inspections) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Overdue */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Atrasadas</span>
                    <span className="text-sm font-bold text-orange-600">
                      {dashboard.overdue || 0} ({dashboard.total_inspections > 0 ? (((dashboard.overdue || 0) / dashboard.total_inspections) * 100).toFixed(0) : 0}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div
                      className="bg-orange-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${dashboard.total_inspections > 0 ? ((dashboard.overdue || 0) / dashboard.total_inspections) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Results Chart */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Resultados das Inspeções
              </h3>
              <div className="space-y-4">
                {/* Approved */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Aprovadas</span>
                    <span className="text-sm font-bold text-green-600">
                      {dashboard.approved} ({dashboard.total_inspections > 0 ? ((dashboard.approved / dashboard.total_inspections) * 100).toFixed(0) : 0}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${dashboard.total_inspections > 0 ? (dashboard.approved / dashboard.total_inspections) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Requires Attention */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Requer Atenção</span>
                    <span className="text-sm font-bold text-yellow-600">
                      {dashboard.requires_attention || 0} ({dashboard.total_inspections > 0 ? (((dashboard.requires_attention || 0) / dashboard.total_inspections) * 100).toFixed(0) : 0}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-yellow-500 to-yellow-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${dashboard.total_inspections > 0 ? ((dashboard.requires_attention || 0) / dashboard.total_inspections) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Failed */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Reprovadas</span>
                    <span className="text-sm font-bold text-red-600">
                      {dashboard.failed} ({dashboard.total_inspections > 0 ? ((dashboard.failed / dashboard.total_inspections) * 100).toFixed(0) : 0}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-red-500 to-red-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${dashboard.total_inspections > 0 ? (dashboard.failed / dashboard.total_inspections) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Approval Rate */}
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Taxa de Aprovação</span>
                    <span className="text-2xl font-bold text-green-600">
                      {dashboard.total_inspections > 0 ? ((dashboard.approved / dashboard.total_inspections) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-gray-600" />
                Estatísticas Rápidas
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total de Inspeções</span>
                  <span className="text-lg font-bold text-gray-900 dark:text-white">{dashboard.total_inspections}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Concluídas com Sucesso</span>
                  <span className="text-lg font-bold text-green-600">{dashboard.approved}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Necessitam Ação</span>
                  <span className="text-lg font-bold text-orange-600">{Number(dashboard.overdue || 0) + Number(dashboard.requires_attention || 0)}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Health Score Médio</span>
                  <span className="text-lg font-bold text-teal-600">
                    {dashboard.avg_health_score ? Number(dashboard.avg_health_score).toFixed(1) : '0'}/100
                  </span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total em Penalidades</span>
                  <span className="text-lg font-bold text-red-600">
                    {formatCurrency(dashboard.total_penalties || 0)}
                  </span>
                </div>
              </div>
            </div>

            {/* Performance Indicators */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                Indicadores de Performance
              </h3>
              <div className="space-y-4">
                {/* Completion Rate */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Taxa de Conclusão</span>
                    <span className="text-sm font-bold text-blue-600">
                      {dashboard.total_inspections > 0 ? ((dashboard.completed / dashboard.total_inspections) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${dashboard.total_inspections > 0 ? (dashboard.completed / dashboard.total_inspections) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* On-Time Rate */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Taxa No Prazo</span>
                    <span className="text-sm font-bold text-green-600">
                      {dashboard.total_inspections > 0 ? (((dashboard.total_inspections - (dashboard.overdue || 0)) / dashboard.total_inspections) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${dashboard.total_inspections > 0 ? ((dashboard.total_inspections - (dashboard.overdue || 0)) / dashboard.total_inspections) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Quality Score */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Score de Qualidade</span>
                    <span className="text-sm font-bold text-teal-600">
                      {dashboard.avg_health_score ? Number(dashboard.avg_health_score).toFixed(1) : '0'}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-teal-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${dashboard.avg_health_score ? Number(dashboard.avg_health_score) : 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Status Summary */}
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                      <p className="text-2xl font-bold text-green-600">{dashboard.approved}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Aprovadas</p>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
                      <p className="text-2xl font-bold text-red-600">{dashboard.failed}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Reprovadas</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inspections Tab - Layout VIPLOC Profissional */}
      {activeTab === 'inspections' && (
        <div className="space-y-6">
          {/* Cabeçalho com Filtros */}
          {(() => {
            const q = filterSearch.toLowerCase().trim();
            const filteredInspections = inspections.filter(insp => {
              if (q) {
                const plate = (insp.vehicle_plate || insp.vehicle_license_plate || insp.license_plate || '').toLowerCase();
                const model = (insp.vehicle_model || '').toLowerCase();
                const brand = (insp.vehicle_brand || '').toLowerCase();
                const client = (insp.client_name || '').toLowerCase();
                if (!plate.includes(q) && !model.includes(q) && !brand.includes(q) && !client.includes(q)) return false;
              }
              if (filterCategory !== 'all') {
                if ((insp.vehicle_category || '') !== filterCategory) return false;
              }
              return true;
            });
            const hasFilters = filterStatus !== 'all' || filterCategory !== 'all' || dateRange.from || filterSearch;

            return (
              <>
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4" style={{ borderTop: '4px solid #2B3E5C' }}>
                  <div className="flex flex-col gap-3">
                    {/* Linha 1: título + contagem */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-bold" style={{ color: '#2B3E5C' }}>Lista de Inspeções</h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {filteredInspections.length !== inspections.length
                            ? `${filteredInspections.length} de ${inspections.length} inspeção(ões)`
                            : `${inspections.length} inspeção(ões)`}
                        </p>
                      </div>
                      {hasFilters && (
                        <button
                          onClick={() => { setFilterStatus('all'); setFilterCategory('all'); setDateRange({ preset: '', from: '', to: '' }); setFilterSearch(''); }}
                          className="px-3 py-1.5 rounded-lg text-sm font-medium border border-red-300 bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 transition-colors"
                        >
                          ✕ Limpar filtros
                        </button>
                      )}
                    </div>

                    {/* Linha 2: todos os filtros na mesma linha */}
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                      {/* Busca texto */}
                      <div className="relative md:col-span-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={filterSearch}
                          onChange={(e) => setFilterSearch(e.target.value)}
                          placeholder="Placa, veículo ou locatário..."
                          className={`w-full pl-9 pr-4 py-2 border-2 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none transition ${filterSearch ? 'border-orange-400' : 'border-gray-200 dark:border-gray-600 focus:border-orange-400'}`}
                        />
                      </div>

                      {/* Status */}
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className={`px-3 py-2 border-2 rounded-lg bg-white dark:bg-gray-700 text-sm focus:border-[#FF9933] transition-colors ${filterStatus !== 'all' ? 'border-orange-400' : 'border-gray-200 dark:border-gray-600'}`}
                      >
                        <option value="all">Todos os status</option>
                        <option value="scheduled">Agendado</option>
                        <option value="in_progress">Em Andamento</option>
                        <option value="completed">Concluído</option>
                        <option value="approved">Aprovado</option>
                        <option value="failed">Reprovado</option>
                      </select>

                      {/* Tipo de veículo */}
                      <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className={`px-3 py-2 border-2 rounded-lg bg-white dark:bg-gray-700 text-sm focus:border-[#FF9933] transition-colors ${filterCategory !== 'all' ? 'border-orange-400' : 'border-gray-200 dark:border-gray-600'}`}
                      >
                        <option value="all">Todos os tipos</option>
                        <option value="pintura_frio">Caminhão de Pintura</option>
                        <option value="munck">Munck / Guindaste</option>
                        <option value="bate_estaca">Bate-Estaca</option>
                        <option value="carroceria">Carroceria</option>
                      </select>

                      {/* Data agendada */}
                      <DateRangePicker value={dateRange} onChange={setDateRange} />
                    </div>
                  </div>
                </div>

                {/* Lista de Inspeções */}
                {/* Tabela compacta */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 dark:border-gray-700" style={{ backgroundColor: '#F7F9FB' }}>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Veículo</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Locatário</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tipo</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Data</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Fotos/Itens</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {filteredInspections.map((inspection) => {
                        const borderColor =
                          inspection.result === 'approved' || inspection.status === 'approved' ? '#22c55e' :
                          inspection.result === 'failed' || inspection.status === 'failed' ? '#ef4444' :
                          inspection.status === 'in_progress' ? '#f59e0b' :
                          inspection.status === 'completed' ? '#22c55e' : '#2B3E5C';

                        const plate = inspection.vehicle_plate || inspection.vehicle_license_plate || inspection.license_plate || 'N/A';
                        const model = [inspection.vehicle_brand, inspection.vehicle_model].filter(Boolean).join(' ') || '—';
                        const dateStr = inspection.scheduled_date || inspection.created_at;
                        const dateFormatted = dateStr
                          ? new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
                          : '—';

                        return (
                          <tr
                            key={inspection.id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                            style={{ borderLeft: `3px solid ${borderColor}` }}
                            onClick={() => handleViewInspection(inspection)}
                          >
                            {/* Veículo */}
                            <td className="px-4 py-3">
                              <p className="font-semibold text-gray-900 dark:text-white">{plate}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{model}</p>
                            </td>

                            {/* Locatário + Motorista */}
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-900 dark:text-white truncate max-w-[180px]">{inspection.client_name || '—'}</p>
                              {inspection.driver_name && (
                                <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[180px]">
                                  <span className="text-gray-400">Mot:</span> {inspection.driver_name}
                                </p>
                              )}
                            </td>

                            {/* Tipo/Template */}
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-900 dark:text-white">{inspection.template_name || 'Inspeção'}</p>
                              <span className="inline-block mt-0.5 px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: '#FFF5EB', color: '#FF9933' }}>
                                {getCategoryLabel(inspection.category || inspection.template_category)}
                              </span>
                            </td>

                            {/* Data */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <p className="font-medium text-gray-900 dark:text-white">{dateFormatted}</p>
                              {inspection.completed_at && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  Conc: {new Date(inspection.completed_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                </p>
                              )}
                            </td>

                            {/* Status */}
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(inspection.status)}`}>
                                {getStatusLabel(inspection.status)}
                              </span>
                              {inspection.result && (
                                <span className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  inspection.result === 'approved' ? 'bg-green-100 text-green-700' :
                                  inspection.result === 'failed' ? 'bg-red-100 text-red-700' :
                                  'bg-orange-100 text-orange-700'
                                }`}>
                                  {inspection.result === 'approved' ? '✓ Aprovado' : inspection.result === 'failed' ? '✗ Reprovado' : '⚠ Atenção'}
                                </span>
                              )}
                              {(inspection.clicksign_status === 'running' || inspection.clicksign_status === 'closed') && (
                                <p className={`mt-1 text-xs font-medium ${inspection.clicksign_status === 'closed' ? 'text-green-600' : 'text-blue-600'}`}>
                                  ✍️ {Number(inspection.signed_count || 0)}/{Number(inspection.total_signers || 0)} assin.
                                </p>
                              )}
                            </td>

                            {/* Fotos / Itens */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <p className="text-gray-700 dark:text-gray-300">
                                <Camera className="w-3 h-3 inline mr-1 text-gray-400" />{inspection.photos_count || 0}
                              </p>
                              <p className="text-gray-700 dark:text-gray-300 mt-0.5">
                                <ClipboardCheck className="w-3 h-3 inline mr-1 text-gray-400" />{inspection.items_count || 0}
                                {inspection.failed_items_count > 0 && (
                                  <span className="text-red-500 ml-1">({inspection.failed_items_count}✗)</span>
                                )}
                              </p>
                            </td>

                            {/* Ação */}
                            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => handleViewInspection(inspection)}
                                className="p-2 rounded-lg transition-colors hover:bg-orange-100 dark:hover:bg-orange-900/30"
                                title="Ver Relatório"
                              >
                                <Eye className="w-4 h-4" style={{ color: '#FF9933' }} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {filteredInspections.length === 0 && (
                    <div className="text-center py-16">
                      <FileText className="w-10 h-10 mx-auto mb-3" style={{ color: '#FF9933' }} />
                      <h3 className="text-lg font-bold mb-1" style={{ color: '#2B3E5C' }}>Nenhuma inspeção encontrada</h3>
                      <p className="text-gray-500 text-sm">Ajuste os filtros ou crie uma nova inspeção</p>
                    </div>
                  )}
                </div>
            </>
          );
          })()}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Templates de Inspeção</h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Crie e gerencie templates reutilizáveis
              </p>
            </div>
            <button
              onClick={handleCreateTemplate}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Criar Template
            </button>
          </div>

          {/* Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              <div className="col-span-4">Nome</div>
              <div className="col-span-2">Categoria</div>
              <div className="col-span-2">Tipo Veículo</div>
              <div className="col-span-2">Vistoria</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1 text-right">Ações</div>
            </div>

            {templates.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400 mb-4">Nenhum template encontrado</p>
                <button
                  onClick={handleCreateTemplate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Criar Primeiro Template
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {templates.map((template) => {
                  const isInactive = template.active === false || template.is_active === false;
                  const roleLabels = { saida: '🚀 Saída', devolucao: '🔄 Devolução', periodica: '📅 Periódica' };
                  const roleColors = {
                    saida: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
                    devolucao: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
                    periodica: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
                  };
                  const vcLabels = { pintura_frio: '🎨 Pintura', munck: '🏗️ Munck', bate_estaca: '⚙️ Bate-Estaca', carroceria: '🚛 Carroceria', all: '—' };

                  return (
                    <div
                      key={template.id}
                      className={`grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors ${isInactive ? 'opacity-50' : ''}`}
                    >
                      {/* Name */}
                      <div className="col-span-4">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{template.name}</p>
                        {template.description && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{template.description}</p>
                        )}
                      </div>

                      {/* Category */}
                      <div className="col-span-2 text-sm text-gray-600 dark:text-gray-400">
                        {getCategoryLabel(template.category)}
                      </div>

                      {/* Vehicle type */}
                      <div className="col-span-2 text-sm text-gray-600 dark:text-gray-400">
                        {vcLabels[template.vehicle_category] || (template.vehicle_type && template.vehicle_type !== 'all' ? template.vehicle_type : '—')}
                      </div>

                      {/* Inspection role */}
                      <div className="col-span-2">
                        {template.inspection_role ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${roleColors[template.inspection_role] || 'bg-gray-100 text-gray-600'}`}>
                            {roleLabels[template.inspection_role] || template.inspection_role}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
                        )}
                      </div>

                      {/* Status */}
                      <div className="col-span-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          isInactive
                            ? 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                            : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        }`}>
                          {isInactive ? 'Inativo' : 'Ativo'}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="col-span-1 flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleStartInspection(template)}
                          disabled={isInactive}
                          title="Iniciar inspeção"
                          className="p-1.5 rounded text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditTemplate(template)}
                          title="Editar template"
                          className="p-1.5 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleTemplate(template.id, template.is_active ?? template.active)}
                          title={isInactive ? 'Ativar template' : 'Desativar template'}
                          className={`p-1.5 rounded transition-colors ${
                            isInactive
                              ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                              : 'text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
                          }`}
                        >
                          <Power className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pending & Overdue Tabs - Layout VIPLOC Profissional */}
      {(activeTab === 'pending' || activeTab === 'overdue') && (
        <div className="space-y-6">
          {/* Cabeçalho */}
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            style={{ borderTop: `4px solid ${activeTab === 'overdue' ? '#ef4444' : '#f59e0b'}` }}
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: activeTab === 'overdue' ? '#fef2f2' : '#fefce8' }}
                >
                  {activeTab === 'overdue' ? (
                    <AlertTriangle className="w-7 h-7 text-red-500" />
                  ) : (
                    <Clock className="w-7 h-7 text-yellow-500" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold" style={{ color: '#2B3E5C' }}>
                    {activeTab === 'pending' ? 'Inspeções Pendentes' : 'Inspeções Atrasadas'}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {inspections.length} inspeção(ões) {activeTab === 'pending' ? 'nos próximos 30 dias' : 'em atraso'}
                  </p>
                </div>
              </div>

              {activeTab === 'overdue' && inspections.length > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 border border-red-200">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <span className="text-sm font-medium text-red-700">
                    Atenção: {inspections.length} inspeção(ões) precisam de ação imediata
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Lista de Inspeções */}
          <div className="grid gap-4">
            {inspections.map((inspection) => {
              // Calcular dias de atraso
              const scheduledDate = new Date(inspection.scheduled_date);
              const today = new Date();
              const daysOverdue = Math.floor((today - scheduledDate) / (1000 * 60 * 60 * 24));
              const daysUntil = Math.floor((scheduledDate - today) / (1000 * 60 * 60 * 24));

              // Determinar cor da borda baseado no atraso/urgência
              const getBorderColor = () => {
                if (activeTab === 'overdue') {
                  if (daysOverdue > 7) return '#dc2626'; // vermelho escuro
                  if (daysOverdue > 3) return '#ef4444'; // vermelho
                  return '#f97316'; // laranja
                }
                if (daysUntil <= 3) return '#f59e0b'; // amarelo
                if (daysUntil <= 7) return '#fbbf24'; // amarelo claro
                return '#2B3E5C'; // navy
              };

              return (
                <div
                  key={inspection.id}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
                  style={{ borderLeft: `4px solid ${getBorderColor()}` }}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      {/* Conteúdo Principal */}
                      <div className="flex-1">
                        {/* Cabeçalho do Card */}
                        <div className="flex items-start gap-4 mb-4">
                          {/* Ícone */}
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: activeTab === 'overdue' ? '#fef2f2' : '#fefce8' }}
                          >
                            {activeTab === 'overdue' ? (
                              <AlertTriangle className="w-6 h-6 text-red-500" />
                            ) : (
                              <Clock className="w-6 h-6 text-yellow-500" />
                            )}
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center gap-3 flex-wrap">
                              <h3 className="text-lg font-bold" style={{ color: '#2B3E5C' }}>
                                {inspection.template_name || 'Inspeção'}
                              </h3>
                              <span className="text-sm text-gray-500">#{inspection.id}</span>
                            </div>

                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(inspection.status)}`}>
                                {getStatusLabel(inspection.status)}
                              </span>
                              <span
                                className="px-3 py-1 rounded-full text-xs font-medium"
                                style={{ backgroundColor: '#FFF5EB', color: '#FF9933' }}
                              >
                                {getCategoryLabel(inspection.category || inspection.template_category)}
                              </span>

                              {/* Badge de Urgência */}
                              {activeTab === 'overdue' ? (
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" />
                                  {daysOverdue} dia(s) em atraso
                                </span>
                              ) : daysUntil <= 3 ? (
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {daysUntil === 0 ? 'Hoje!' : daysUntil === 1 ? 'Amanhã' : `Em ${daysUntil} dias`}
                                </span>
                              ) : (
                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Em {daysUntil} dias
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Informações Detalhadas */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
                          {/* Veículo */}
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                              <FileText className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Veículo</p>
                              <p className="font-semibold" style={{ color: '#2B3E5C' }}>
                                {inspection.vehicle_plate || inspection.vehicle_license_plate || inspection.license_plate || 'N/A'}
                              </p>
                            </div>
                          </div>

                          {/* Cliente */}
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                              <User className="w-4 h-4 text-indigo-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs text-gray-500">Cliente</p>
                              <p className="font-semibold truncate" style={{ color: '#2B3E5C' }}>
                                {inspection.client_name || '—'}
                              </p>
                            </div>
                          </div>

                          {/* Motorista */}
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
                              <User className="w-4 h-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Motorista</p>
                              <p className="font-semibold" style={{ color: '#2B3E5C' }}>
                                {inspection.driver_name || 'N/A'}
                              </p>
                            </div>
                          </div>

                          {/* Data Prevista */}
                          <div className="flex items-center gap-2">
                            <div
                              className="w-8 h-8 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: activeTab === 'overdue' ? '#fef2f2' : '#f0fdf4' }}
                            >
                              <Calendar
                                className="w-4 h-4"
                                style={{ color: activeTab === 'overdue' ? '#ef4444' : '#22c55e' }}
                              />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Data Prevista</p>
                              <p
                                className="font-semibold"
                                style={{ color: activeTab === 'overdue' ? '#ef4444' : '#2B3E5C' }}
                              >
                                {new Date(inspection.scheduled_date).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                          </div>

                          {/* Data Limite */}
                          {inspection.due_date && (
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                                <Clock className="w-4 h-4 text-orange-600" />
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Limite</p>
                                <p className="font-semibold" style={{ color: '#2B3E5C' }}>
                                  {new Date(inspection.due_date).toLocaleDateString('pt-BR')}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Penalidade (se atrasado) */}
                          {activeTab === 'overdue' && inspection.calculated_penalty > 0 && (
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                                <AlertTriangle className="w-4 h-4 text-red-600" />
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Penalidade</p>
                                <p className="font-semibold text-red-600">
                                  R$ {Number(inspection.calculated_penalty).toFixed(2)}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Botão Ver Relatório */}
                      <button
                        onClick={() => handleViewInspection(inspection)}
                        className="ml-4 px-4 py-3 rounded-xl font-medium transition-all flex items-center gap-2 group-hover:scale-105"
                        style={{
                          backgroundColor: '#FF9933',
                          color: 'white'
                        }}
                      >
                        <Eye className="w-5 h-5" />
                        <span className="hidden md:inline">Ver Detalhes</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {inspections.length === 0 && (
              <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
                <div
                  className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                  style={{ backgroundColor: '#f0fdf4' }}
                >
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#2B3E5C' }}>
                  {activeTab === 'pending' ? 'Tudo em dia!' : 'Nenhum atraso!'}
                </h3>
                <p className="text-gray-500">
                  {activeTab === 'pending'
                    ? 'Não há inspeções pendentes nos próximos 30 dias'
                    : 'Parabéns! Não há inspeções atrasadas'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full my-8">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {editingTemplate ? 'Editar Template' : 'Novo Template de Inspeção'}
                </h2>
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nome do Template *
                    </label>
                    <input
                      type="text"
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                      placeholder="Ex: Inspeção Diária"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Categoria *
                    </label>
                    <select
                      value={templateForm.category}
                      onChange={(e) => setTemplateForm({...templateForm, category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    >
                      <option value="checklist">Checklist (DVIR)</option>
                      <option value="vehicle_inspection">Inspeção de Veículo</option>
                      <option value="contract_inspection">Inspeção de Contrato</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tipo de Veículo
                    </label>
                    <select
                      value={templateForm.vehicle_type}
                      onChange={(e) => setTemplateForm({...templateForm, vehicle_type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Selecione um tipo</option>
                      {vehicleTypes.map(type => (
                        <option key={type.id || type} value={type.name || type}>
                          {type.display_name || type.name || type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-6 pt-8">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={templateForm.requires_signature}
                        onChange={(e) => setTemplateForm({...templateForm, requires_signature: e.target.checked})}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Requer Assinatura
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={templateForm.requires_video}
                        onChange={(e) => setTemplateForm({...templateForm, requires_video: e.target.checked})}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Requer Vídeo
                      </span>
                    </label>
                    <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                      (Fotos obrigatórias são definidas por item em cada seção)
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Descrição
                  </label>
                  <textarea
                    value={templateForm.description}
                    onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    rows={3}
                    placeholder="Descrição do template..."
                  />
                </div>

                {/* Sections */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Seções
                    </h3>
                    <button
                      onClick={() => {
                        const newSections = [...templateForm.sections, {
                          name: '',
                          display_order: templateForm.sections.length + 1,
                          items: []
                        }];
                        setTemplateForm({...templateForm, sections: newSections});
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Adicionar Seção
                    </button>
                  </div>

                  {templateForm.sections.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 dark:bg-gray-700/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                      <p className="text-gray-500 dark:text-gray-400">
                        Nenhuma seção adicionada. Clique em "Adicionar Seção" para começar.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {templateForm.sections.map((section, sectionIndex) => (
                        <div key={sectionIndex} className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50">
                          <div className="flex items-start gap-4">
                            <div className="flex-1">
                              {/* Cabeçalho da Seção */}
                              <div className="flex items-center gap-3 mb-3">
                                <input
                                  type="text"
                                  value={section.name}
                                  onChange={(e) => {
                                    const newSections = [...templateForm.sections];
                                    newSections[sectionIndex].name = e.target.value;
                                    setTemplateForm({...templateForm, sections: newSections});
                                  }}
                                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                  placeholder="Nome da seção (ex: Verificação Externa)"
                                />
                                {/* Botão Selecionar Todos para Foto */}
                                {section.items && section.items.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => toggleSectionPhotos(sectionIndex, !allSectionPhotosRequired(sectionIndex))}
                                    className={`flex items-center gap-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors whitespace-nowrap ${
                                      allSectionPhotosRequired(sectionIndex)
                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200'
                                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200'
                                    }`}
                                    title={allSectionPhotosRequired(sectionIndex) ? 'Desmarcar todas as fotos' : 'Marcar todas as fotos'}
                                  >
                                    <Camera className="w-4 h-4" />
                                    {allSectionPhotosRequired(sectionIndex) ? 'Desmarcar Fotos' : 'Marcar Fotos'}
                                  </button>
                                )}
                              </div>

                              {/* Items in Section */}
                              <div className="space-y-2">
                                {section.items && section.items.map((item, itemIndex) => (
                                  <div key={itemIndex} className="flex items-center gap-2 bg-white dark:bg-gray-800 p-2 rounded-lg">
                                    <input
                                      type="text"
                                      value={item.name || ''}
                                      onChange={(e) => {
                                        const newSections = [...templateForm.sections];
                                        newSections[sectionIndex].items[itemIndex].name = e.target.value;
                                        setTemplateForm({...templateForm, sections: newSections});
                                      }}
                                      className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                                      placeholder="Nome do item"
                                    />
                                    <select
                                      value={item.field_type}
                                      onChange={(e) => {
                                        const newSections = [...templateForm.sections];
                                        newSections[sectionIndex].items[itemIndex].field_type = e.target.value;
                                        setTemplateForm({...templateForm, sections: newSections});
                                      }}
                                      className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                                    >
                                      <option value="ok_fail">OK/Falha</option>
                                      <option value="numeric">Numérico</option>
                                      <option value="text">Texto</option>
                                      <option value="scale">Escala (1-5)</option>
                                    </select>
                                    {/* Toggle Foto Obrigatória por Item */}
                                    <label
                                      className={`flex items-center gap-1 px-2 py-1 text-xs rounded cursor-pointer transition-colors ${
                                        item.require_photo_if_fail
                                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                          : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                                      }`}
                                      title={item.require_photo_if_fail ? 'Foto obrigatória' : 'Sem foto obrigatória'}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={item.require_photo_if_fail || false}
                                        onChange={(e) => {
                                          const newSections = [...templateForm.sections];
                                          newSections[sectionIndex].items[itemIndex].require_photo_if_fail = e.target.checked;
                                          setTemplateForm({...templateForm, sections: newSections});
                                        }}
                                        className="w-3 h-3 text-green-600 rounded"
                                      />
                                      <Camera className="w-3 h-3" />
                                    </label>
                                    <button
                                      onClick={() => {
                                        const newSections = [...templateForm.sections];
                                        newSections[sectionIndex].items.splice(itemIndex, 1);
                                        setTemplateForm({...templateForm, sections: newSections});
                                      }}
                                      className="text-red-600 hover:text-red-700 p-1"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}

                                <button
                                  onClick={() => {
                                    const newSections = [...templateForm.sections];
                                    if (!newSections[sectionIndex].items) {
                                      newSections[sectionIndex].items = [];
                                    }
                                    newSections[sectionIndex].items.push({
                                      name: '',
                                      description: '',
                                      field_type: 'ok_fail',
                                      is_required: true,
                                      require_photo_if_fail: false,
                                      display_order: newSections[sectionIndex].items.length + 1
                                    });
                                    setTemplateForm({...templateForm, sections: newSections});
                                  }}
                                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                                >
                                  <Plus className="w-4 h-4" />
                                  Adicionar Item
                                </button>
                              </div>
                            </div>

                            <button
                              onClick={() => {
                                const newSections = templateForm.sections.filter((_, i) => i !== sectionIndex);
                                setTemplateForm({...templateForm, sections: newSections});
                              }}
                              className="text-red-600 hover:text-red-700 p-2"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Salvar Template
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inspection Modal */}
      {showInspectionModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full my-8">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Nova Inspeção: {selectedTemplate.name}
                </h2>
                <button
                  onClick={() => setShowInspectionModal(false)}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Veículo *
                  </label>
                  <select
                    value={inspectionForm.vehicle_id}
                    onChange={(e) => setInspectionForm({...inspectionForm, vehicle_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    required
                  >
                    <option value="">Selecione um veículo</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.license_plate} - {v.model}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Motorista
                  </label>
                  <select
                    value={inspectionForm.driver_id}
                    onChange={(e) => setInspectionForm({...inspectionForm, driver_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                  >
                    <option value="">Selecione um motorista</option>
                    {drivers.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Hodômetro (km)
                  </label>
                  <input
                    type="number"
                    value={inspectionForm.odometer_reading}
                    onChange={(e) => setInspectionForm({...inspectionForm, odometer_reading: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    placeholder="KM atual"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Horímetro (horas)
                  </label>
                  <input
                    type="number"
                    value={inspectionForm.engine_hours}
                    onChange={(e) => setInspectionForm({...inspectionForm, engine_hours: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    placeholder="Horas de uso"
                  />
                </div>
              </div>

              {/* Inspection Items */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Itens de Inspeção
                </h3>

                {inspectionForm.items.reduce((acc, item, index) => {
                  const lastSection = acc[acc.length - 1];
                  if (!lastSection || lastSection.name !== item.section_name) {
                    acc.push({ name: item.section_name, items: [{ item, index }] });
                  } else {
                    lastSection.items.push({ item, index });
                  }
                  return acc;
                }, []).map((section, sectionIndex) => (
                  <div key={sectionIndex} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                      {section.name}
                    </h4>

                    <div className="space-y-4">
                      {section.items.map(({ item, index }) => (
                        <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 dark:text-white">
                                {item.item_name}
                                {item.is_required && <span className="text-red-500 ml-1">*</span>}
                              </p>
                            </div>
                          </div>

                          {renderFieldInput(item, index)}

                          {/* Comments */}
                          {item.is_approved === false && (
                            <div className="mt-3">
                              <textarea
                                value={item.comments}
                                onChange={(e) => handleItemResponse(index, 'comments', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                rows={2}
                                placeholder="Comentários..."
                              />
                            </div>
                          )}

                          {/* Photo Upload */}
                          {item.require_photo_if_fail && item.is_approved === false && (
                            <div className="mt-3">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                <Camera className="w-4 h-4 inline mr-1" />
                                Fotos <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) => handlePhotoUpload(index, e.target.files)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                              />
                              {item.photos && item.photos.length > 0 && (
                                <div className="flex gap-2 mt-2">
                                  {item.photos.map((photo, i) => (
                                    <img
                                      key={i}
                                      src={photo.preview}
                                      alt="Preview"
                                      className="w-20 h-20 object-cover rounded"
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Signatures */}
              {selectedTemplate.requires_signature && (
                <div className="mt-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Assinatura do Motorista *
                    </label>
                    <div className="border-2 border-gray-300 dark:border-gray-600 rounded-lg">
                      <SignatureCanvas
                        ref={signatureDriverRef}
                        canvasProps={{
                          className: 'w-full h-32 bg-white dark:bg-gray-700',
                        }}
                      />
                    </div>
                    <button
                      onClick={() => signatureDriverRef.current?.clear()}
                      className="text-sm text-blue-600 hover:text-blue-700 mt-1"
                    >
                      Limpar assinatura
                    </button>
                  </div>
                </div>
              )}

              {/* Condutor e Recebedor */}
              <div className="mt-6 rounded-xl border-2 border-blue-200 dark:border-blue-800 p-5 bg-blue-50 dark:bg-blue-900/20">
                <h4 className="text-lg font-bold text-[#2B3E5C] dark:text-blue-300 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#FF9933]" />
                  Entrega do Veículo
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nome do Condutor (quem entrega)
                    </label>
                    <input
                      type="text"
                      value={inspectionForm.condutor_nome}
                      onChange={(e) => setInspectionForm({...inspectionForm, condutor_nome: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                      placeholder="Nome completo do condutor..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nome do Recebedor (quem recebe)
                    </label>
                    <input
                      type="text"
                      value={inspectionForm.recebedor_nome}
                      onChange={(e) => setInspectionForm({...inspectionForm, recebedor_nome: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                      placeholder="Nome completo do recebedor..."
                    />
                  </div>
                </div>

                {/* Checklist de Entrega */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Checklist de Entrega
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {[
                      { key: 'documentos', label: 'Documentos do veículo (CRLV, seguro)' },
                      { key: 'chaves', label: 'Chaves do veículo (principal + reserva)' },
                      { key: 'estepe', label: 'Estepe / pneu reserva' },
                      { key: 'macaco', label: 'Macaco e chave de roda' },
                      { key: 'triangulo', label: 'Triângulo de sinalização' },
                      { key: 'extintor', label: 'Extintor de incêndio' },
                      { key: 'manual', label: 'Manual do veículo' },
                      { key: 'combustivel', label: 'Nível de combustível verificado' },
                      { key: 'limpeza', label: 'Veículo limpo e higienizado' },
                      { key: 'acessorios', label: 'Acessórios conferidos' },
                    ].map(item => (
                      <label
                        key={item.key}
                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/30 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={inspectionForm.checklist_entrega[item.key] || false}
                          onChange={(e) => setInspectionForm({
                            ...inspectionForm,
                            checklist_entrega: {
                              ...inspectionForm.checklist_entrega,
                              [item.key]: e.target.checked
                            }
                          })}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{item.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Assinaturas Condutor e Recebedor */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Assinatura do Condutor
                    </label>
                    <div className="border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white">
                      <SignatureCanvas
                        ref={signatureCondutorRef}
                        canvasProps={{
                          className: 'w-full h-32',
                        }}
                      />
                    </div>
                    <button
                      onClick={() => signatureCondutorRef.current?.clear()}
                      className="text-sm text-blue-600 hover:text-blue-700 mt-1"
                    >
                      Limpar assinatura
                    </button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Assinatura do Recebedor
                    </label>
                    <div className="border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white">
                      <SignatureCanvas
                        ref={signatureRecebedorRef}
                        canvasProps={{
                          className: 'w-full h-32',
                        }}
                      />
                    </div>
                    <button
                      onClick={() => signatureRecebedorRef.current?.clear()}
                      className="text-sm text-blue-600 hover:text-blue-700 mt-1"
                    >
                      Limpar assinatura
                    </button>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Observações Gerais
                </label>
                <textarea
                  value={inspectionForm.notes}
                  onChange={(e) => setInspectionForm({...inspectionForm, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                  rows={3}
                  placeholder="Observações adicionais..."
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowInspectionModal(false)}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmitInspection}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Finalizar Inspeção
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Relatório Completo da Inspeção */}
      {showViewModal && selectedInspection && (
        <InspectionReportModal
          inspection={selectedInspection}
          onClose={() => {
            setShowViewModal(false);
            setSelectedInspection(null);
          }}
        />
      )}
    </div>
  );
};

export default InspectionsPage;
